<template>
  <RevLink :to :underlined="false" class="hover:no-underline">
    <RevCard class="flex h-full flex-col gap-12 p-16" hoverable>
      <RevTag
        :label="tagLabel"
        size="small"
        :variant="tagVariant"
        class="w-fit self-center"
      />

      <p v-if="dateLabel" class="body-2 text-static-default-low self-center">
        {{ dateLabel }}
      </p>

      <div class="flex grow items-center justify-center py-8">
        <RevIllustration
          :alt="imageAlt"
          :height="120"
          :src="imageUrl"
          :width="120"
          loading="lazy"
        />
      </div>

      <div class="flex flex-col gap-4">
        <p v-if="subtitle" class="body-2 text-static-default-low">
          {{ subtitle }}
        </p>
        <p class="body-1-bold text-static-default-hi">{{ title }}</p>
      </div>
    </RevCard>
  </RevLink>
</template>

<script setup lang="ts">
import { RevCard } from '@ds/components/Card'
import { RevIllustration } from '@ds/components/Illustration'
import { RevLink } from '@ds/components/Link'
import { RevTag, type Variants as TagVariant } from '@ds/components/Tag'
import type { Location } from '@ds/types/Location'

type Props = {
  /** Status label text, e.g. "Delivered", "Trade-in created", "Pending start" */
  tagLabel: string
  /** Tag colour variant — resolved by the parent from the backend status */
  tagVariant: TagVariant

  /**
   * Optional date / deadline line shown beneath the tag.
   * e.g. "On: Friday, Jul. 31" or "19 days left to ship".
   */
  dateLabel?: string

  /** Product photo or brand logo URL */
  imageUrl: string
  /** Accessible alt text for the image (empty string if decorative) */
  imageAlt: string

  /** Main card title — product name or plan name */
  title: string
  /**
   * Optional secondary line — backend-driven tag ("Replacement order",
   * "Canceled by seller") or the device covered by a service plan.
   */
  subtitle?: string

  /** Navigation target. Accepts a string URL or a vue-router Location object. */
  to: Location
}

defineProps<Props>()
</script>
